/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import React from 'react';
import { IAction, IDispatch, INext } from '../interfaces';
import { TContextProvider } from '@msdyn365-commerce-modules/chat';
import { IPvaChatConnectorViewProps } from '../pva-chat-connector';

export class CommerceChatStore {
    private props: IPvaChatConnectorViewProps;
    private setNewMessageAction: React.Dispatch<React.SetStateAction<boolean>>;
    private provider?: TContextProvider;

    /**
     * Gets conversation token
     * @param props IPvaChatConnectorViewProps
     */
    public static async getDirectLineToken(props: IPvaChatConnectorViewProps): Promise<string> {
        if (props.config.powervaDirectlineTokenURL && props.config.botID && props.config.tenantID) {
            // use direct line token url, bot id and tenant id from module config to get conversation token
            const url = `${props.config.powervaDirectlineTokenURL}?botId=${props.config.botID}&tenantId=${props.config.tenantID}`;
            const options = { method: 'GET' };
            return fetch(url, options)
                .then(r => r.json())
                .then(r => {
                    if (r.token) {
                        return r.token;
                    } else {
                        throw r;
                    }
                });
        }

        // use direct line token url, bot id and tenant id from app config to get conversation token
        const url = `${props.context.app.config.pvaChatConnectorPowervaDirectlineTokenURL}?botId=${props.context.app.config.pvaChatConnectorBotID}&tenantId=${props.context.app.config.pvaChatConnectorTenantID}`;
        const options = {
            method: 'GET'
        };
        return fetch(url, options)
            .then(r => r.json())
            .then(r => {
                if (r.token) {
                    return r.token;
                } else {
                    throw r;
                }
            });
    }

    /**
     * Creates a new instance of commerce store for WebChat
     * @param props IPvaChatConnectorViewProps
     * @param provider TContextProvider
     * @param setNewMessage React.Dispatch<React.SetStateAction<boolean>>
     */
    constructor(
        props: IPvaChatConnectorViewProps,
        provider: TContextProvider | undefined,
        setNewMessage: React.Dispatch<React.SetStateAction<boolean>>
    ) {
        this.props = props;
        this.provider = provider;
        this.setNewMessageAction = setNewMessage;
    }

    /**
     * Handles custom chat actions
     * @param param0 {
     *      dispatch: IDispatch;
     *      next: INext;
     *      action: IAction;
     *  }
     */
    public async actionHandler({ dispatch, next, action }: { dispatch: IDispatch; next: INext; action: IAction }): Promise<IDispatch> {
        const atype = action.type;

        switch (atype) {
            case 'DIRECT_LINE/CONNECT_FULFILLED': {
                // sets language
                dispatch({
                    type: 'WEB_CHAT/SEND_EVENT',
                    payload: {
                        name: 'webchat/join',
                        value: { language: window.navigator.language }
                    }
                });
                break;
            }
            case 'DIRECT_LINE/INCOMING_ACTIVITY': {
                const activity = action.payload.activity;
                if (activity?.from?.role === 'bot') {
                    // Sets flag for new message (will be use to indicate new messages from bot when chat is minimized)
                    this.setNewMessageAction(true);
                }
                break;
            }
            case 'DIRECT_LINE/POST_ACTIVITY_FULFILLED': {
                switch (action.payload.activity.name) {
                    case 'webchat/join':
                        {
                            if (this.provider !== undefined) {
                                // Sets Chat Context
                                dispatch({
                                    type: 'WEB_CHAT/SEND_EVENT',
                                    payload: {
                                        name: 'pvaSetContext',
                                        value: this.provider()
                                    }
                                });
                            }
                        }
                        break;
                    case 'pvaSetContext':
                        {
                            if (this.props.config.triggerQuery) {
                                // Initialize chat after Chat Context is set
                                dispatch({
                                    type: 'WEB_CHAT/SEND_MESSAGE_BACK',
                                    payload: {
                                        text: this.props.config.triggerQuery
                                    }
                                });
                            }
                        }
                        break;
                    default: {
                        // default nothing
                    }
                }
                break;
            }
            default: {
                // default nothing
            }
        }

        return next(action);
    }
}
